import _extends from "@babel/runtime/helpers/extends";
import { Box, Text, Tooltip } from '@wix/design-system';
import { debounce } from './DomainTruncationHelpers';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import s from './DomainWithEllipsis.scss';
import { useDomainTruncation, useTextMeasurement } from './hooks';
export const DomainWithEllipsis = _ref => {
  let {
    className,
    children,
    maxWidth,
    dataHook,
    textComponent,
    textComponentProps
  } = _ref;
  const domainStr = (children == null ? void 0 : children.toString()) ?? '';
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const hiddenTextRef = useRef(null);
  const Component = textComponent || Text;
  const [mounted, setMounted] = useState(false);

  // Use refs to maintain stable values and prevent oscillation
  const lastCalculationRef = useRef({
    domainStr: '',
    containerWidth: 0,
    isTruncated: false,
    truncatedText: ''
  });

  // Flag to prevent multiple calculations in same cycle
  const calculationInProgressRef = useRef(false);

  // Get text measurement utilities
  const {
    measureTextWidth
  } = useTextMeasurement(hiddenTextRef);

  // Get domain truncation utilities
  const {
    calculateTruncatedDomain,
    domain
  } = useDomainTruncation({
    domainStr,
    measureTextWidth
  });

  // Local state for display
  const [state, setState] = useState({
    isTruncated: false,
    truncatedText: domainStr
  });

  // Calculate truncation with stability checks
  const calculateStableTruncation = useCallback(function (forceCalculation) {
    if (forceCalculation === void 0) {
      forceCalculation = false;
    }
    if (!containerRef.current || !textRef.current || !hiddenTextRef.current || calculationInProgressRef.current) {
      return;
    }

    // Set flag to prevent recursive calls
    calculationInProgressRef.current = true;
    try {
      const containerWidth = containerRef.current.clientWidth;
      const lastCalc = lastCalculationRef.current;

      // Skip calculation if nothing relevant has changed and we're not forcing
      if (!forceCalculation && lastCalc.domainStr === domainStr && lastCalc.containerWidth === containerWidth) {
        return;
      }

      // The container width changed - we definitely need to recalculate
      const containerWidthChanged = lastCalc.containerWidth !== containerWidth;

      // Calculate based on raw values rather than current state
      const fullDomainWidth = measureTextWidth(domainStr);
      const needsTruncation = Math.round(fullDomainWidth) > containerWidth;
      if (!needsTruncation) {
        // Don't need truncation
        // Only update if state would change or container width changed
        if (lastCalc.isTruncated || containerWidthChanged || lastCalc.domainStr !== domainStr) {
          lastCalculationRef.current = {
            domainStr,
            containerWidth,
            isTruncated: false,
            truncatedText: domainStr
          };
          setState({
            isTruncated: false,
            truncatedText: domainStr
          });
        }
      } else {
        // Need truncation
        const truncatedText = calculateTruncatedDomain(domainStr, containerWidth);

        // Only update if the truncated text would change or container width changed
        if (lastCalc.truncatedText !== truncatedText || containerWidthChanged || lastCalc.domainStr !== domainStr) {
          // Store calculated values
          lastCalculationRef.current = {
            domainStr,
            containerWidth,
            isTruncated: true,
            truncatedText
          };
          setState({
            isTruncated: true,
            truncatedText
          });
        }
      }
    } finally {
      // Reset flag when done
      calculationInProgressRef.current = false;
    }
  }, [domainStr, calculateTruncatedDomain, measureTextWidth]);

  // Create a debounced version of calculation with improved responsiveness
  const debouncedCalculation = useMemo(() => {
    return debounce(() => calculateStableTruncation(true), 20);
  }, [calculateStableTruncation]);

  // Handle component mounting/unmounting
  useLayoutEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle domain string changes
  useEffect(() => {
    if (mounted) {
      calculateStableTruncation(true);
    }
  }, [domainStr, mounted, calculateStableTruncation]);

  // Set up ResizeObserver to detect container size changes
  useEffect(() => {
    if (!mounted) {
      return;
    }

    // Force initial measurement after mounting
    requestAnimationFrame(() => {
      requestAnimationFrame(() => calculateStableTruncation(true));
    });

    // Set up resize observer with immediate execution
    const resizeObserver = typeof window !== 'undefined' && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      // Force immediate recalculation on resize
      calculateStableTruncation(true);
    }) : null;
    if (containerRef.current) {
      resizeObserver == null || resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver == null || resizeObserver.disconnect();
    };
  }, [mounted, calculateStableTruncation, debouncedCalculation]);
  const textClassNames = useMemo(() => [className].filter(Boolean).join(' '), [className]);

  // Handle invalid domains gracefully
  if (domain.error) {
    return /*#__PURE__*/React.createElement(Component, _extends({}, textComponentProps, {
      className: textClassNames
    }), children);
  }

  // Render hidden text measurement element
  const renderHiddenTextElement = () => /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Component, _extends({
    dataHook: dataHook
  }, textComponentProps, {
    className: textClassNames
  }), /*#__PURE__*/React.createElement("span", {
    ref: hiddenTextRef,
    style: {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap'
    }
  }, domainStr)));

  // Render visible domain text
  const renderVisibleText = () => /*#__PURE__*/React.createElement("span", {
    ref: textRef
  }, /*#__PURE__*/React.createElement(Component, _extends({}, textComponentProps, {
    className: textClassNames
  }), mounted ? state.truncatedText : domainStr));
  return /*#__PURE__*/React.createElement(Box, {
    maxWidth: maxWidth ?? '600px',
    className: s.domainEllipsisContainer,
    inline: true
  }, /*#__PURE__*/React.createElement(Tooltip, {
    content: domainStr,
    disabled: !state.isTruncated,
    maxWidth: "100%"
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    ref: containerRef,
    className: s.container
  }, renderHiddenTextElement()), renderVisibleText())));
};
//# sourceMappingURL=DomainWithEllipsis.js.map